import moment, { Moment } from 'moment';

export type ComboHolidayType = 'FHT' | 'FT' | 'FH';

export const COMBO_HOLIDAY_TYPES = {
  FLIGHT_HOTEL_TICKET: 'FHT' as ComboHolidayType,
  FLIGHT_TICKET: 'FT' as ComboHolidayType,
  FLIGHT_HOTEL: 'FH' as ComboHolidayType,
};

export type Stage = 'one-way' | 'round-trip';

export const isHotelCombo = (comboType: ComboHolidayType): boolean =>
  comboType === COMBO_HOLIDAY_TYPES.FLIGHT_HOTEL_TICKET ||
  comboType === COMBO_HOLIDAY_TYPES.FLIGHT_HOTEL;

type HotelDateFilter = {
  stage: Stage;
  departureDate: string;
  returnDate: string;
  checkin?: string;
  checkout?: string;
};

export const getDefaultHotelDates = (
  filter: Pick<HotelDateFilter, 'stage' | 'departureDate' | 'returnDate'>,
): { checkin: string; checkout: string } => {
  const departure = moment(filter.departureDate);
  if (!departure.isValid()) {
    return { checkin: '', checkout: '' };
  }
  const checkin = departure.format('YYYY-MM-DD');

  if (filter.stage === 'round-trip') {
    if (filter.returnDate) {
      const returnDate = moment(filter.returnDate);
      if (returnDate.isValid() && !departure.isSame(returnDate)) {
        return {
          checkin,
          checkout: returnDate.format('YYYY-MM-DD'),
        };
      }
    }
    return { checkin, checkout: '' };
  }

  return {
    checkin,
    checkout: moment(checkin).add(1, 'day').format('YYYY-MM-DD'),
  };
};

export const calculateHotelNights = (
  checkin: string,
  checkout: string,
): number => {
  const checkIn = moment(checkin);
  const checkOut = moment(checkout);
  if (!checkIn.isValid() || !checkOut.isValid()) return 0;
  const nights = checkOut.diff(checkIn, 'days');
  return nights > 0 ? nights : 0;
};

/** Ngày trả phòng phải sau ngày nhận phòng (MSG026). */
export const isHotelCheckoutInvalid = (
  checkin: string,
  checkout: string,
): boolean => {
  if (!checkin || !checkout) return false;
  const checkIn = moment(checkin);
  const checkOut = moment(checkout);
  if (!checkIn.isValid() || !checkOut.isValid()) return false;
  return checkOut.isSameOrBefore(checkIn, 'day');
};

/** Ngày nhận = ngày đi; khứ hồi: ngày trả = ngày về (hoặc trống nếu chưa chọn); một chiều: ngày trả = ngày đi + 1. */
export const syncHotelDatesWithFlight = (
  filter: Pick<HotelDateFilter, 'stage' | 'departureDate' | 'returnDate'>,
): { checkin: string; checkout: string } => getDefaultHotelDates(filter);

export const getHotelDisabledDate =
  (filter: Pick<HotelDateFilter, 'stage' | 'departureDate' | 'returnDate'>) =>
  (current: Moment | null): boolean => {
    if (!current) return false;
    const departure = moment(filter.departureDate);
    if (departure.isValid() && current.isBefore(departure, 'day')) {
      return true;
    }
    if (filter.stage === 'round-trip' && filter.returnDate) {
      const returnDate = moment(filter.returnDate);
      if (returnDate.isValid() && current.isAfter(returnDate, 'day')) {
        return true;
      }
    }
    return false;
  };

export const ROOM_MIN = 1;
export const ROOM_MAX = 5;
export const ADULT_MAX = 9;
export const PASSENGER_TOTAL_MAX = ADULT_MAX;

export const clampRoomCount = (value: number): number =>
  Math.min(ROOM_MAX, Math.max(ROOM_MIN, +value || ROOM_MIN));

/** Số người lớn tối thiểu khi combo có khách sạn = số phòng. */
export const getAdultMinForCombo = (
  comboType: ComboHolidayType,
  room: number,
): number => (isHotelCombo(comboType) ? Math.max(ROOM_MIN, room) : ROOM_MIN);

/** Tổng NL + TE tối đa = 9. */
export const getMaxPassengersForCombo = (
  _comboType?: ComboHolidayType,
  _room?: number,
): number => PASSENGER_TOTAL_MAX;

export const isPassengerTotalExceeded = (
  adult: number,
  child: number,
): boolean => adult + child > PASSENGER_TOTAL_MAX;

export const shouldShowRoomAutoIncreasePassengerError = (
  adultAutoIncreased: boolean,
  adult: number,
  child: number,
): boolean => adultAutoIncreased && isPassengerTotalExceeded(adult, child);

export const getMaxChildForCombo = (
  adult: number,
  comboType: ComboHolidayType,
  room: number,
): number => Math.max(0, getMaxPassengersForCombo(comboType, room) - adult);

export const getMaxAdultForCombo = (
  child: number,
  comboType: ComboHolidayType,
  room: number,
): number =>
  Math.min(
    ADULT_MAX,
    Math.max(
      getAdultMinForCombo(comboType, room),
      getMaxPassengersForCombo(comboType, room) - child,
    ),
  );

export const clampAdultCount = (
  value: number,
  comboType: ComboHolidayType,
  room: number,
  child = 0,
): number => {
  const min = getAdultMinForCombo(comboType, room);
  const max = getMaxAdultForCombo(child, comboType, room);
  const parsed = +value;
  const safe = Number.isFinite(parsed) ? parsed : min;
  return Math.min(max, Math.max(min, safe <= 0 ? min : safe));
};

export const clampChildCount = (
  value: number,
  adult: number,
  comboType: ComboHolidayType,
  room: number,
): number => {
  const max = getMaxChildForCombo(adult, comboType, room);
  const parsed = +value;
  const safe = Number.isFinite(parsed) ? parsed : 0;
  return Math.min(max, Math.max(0, safe < 0 ? 0 : safe));
};

/** Tăng phòng > người lớn → tự tăng người lớn; giảm phòng không giảm người lớn. */
export const applyRoomChange = (
  roomValue: number,
  adult: number,
): { room: number; adult: number; adultAutoIncreased: boolean } => {
  const room = clampRoomCount(roomValue);
  const nextAdult = adult < room ? room : adult;
  return {
    room,
    adult: nextAdult,
    adultAutoIncreased: nextAdult > adult,
  };
};

export const resolveRoomChangeState = (
  roomValue: number,
  adult: number,
  child: number,
) => {
  const { room, adult: nextAdult } = applyRoomChange(roomValue, adult);
  const maxChild = Math.max(0, PASSENGER_TOTAL_MAX - nextAdult);
  return {
    room,
    adult: nextAdult,
    child: Math.min(child, maxChild),
  };
};

export const isAdultDecrementDisabled = (
  adult: number,
  comboType: ComboHolidayType,
  room: number,
): boolean => adult <= getAdultMinForCombo(comboType, room);
